var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classNames from 'classnames';
import React, { useId } from 'react';
import { Label } from './Label';
import { ErrorFieldMessage } from './message';
import { Tooltip } from './Tooltip';
var getChildrenWithExtraProps = function (children, extraProps) {
    if (typeof children === 'function') {
        return children(extraProps);
    }
    else if (React.isValidElement(children)) {
        return React.cloneElement(children, extraProps);
    }
    return children;
};
export var InputGroup = function (_a) {
    var inputId = _a.inputId, children = _a.children, className = _a.className, _b = _a.extraMargin, extraMargin = _b === void 0 ? true : _b, description = _a.description, label = _a.label, fieldMessage = _a.fieldMessage, tooltip = _a.tooltip, onTooltipToggle = _a.onTooltipToggle, labelId = _a.labelId, rest = __rest(_a, ["inputId", "children", "className", "extraMargin", "description", "label", "fieldMessage", "tooltip", "onTooltipToggle", "labelId"]);
    var generatedInputId = useId();
    var id = inputId !== null && inputId !== void 0 ? inputId : generatedInputId;
    var descriptionId = description ? "".concat(id, "-description") : undefined;
    if (React.Children.count(children) > 1) {
        throw new Error('This element does not support more than one child. If you need more than one element inside your ' +
            'InputGroup, please use the function-as-a-child pattern outlined in the documentation.');
    }
    var getFieldMessageId = function () {
        var _a;
        if (typeof fieldMessage === 'string') {
            return "".concat(id, "-fieldmessage");
        }
        else if ((_a = fieldMessage === null || fieldMessage === void 0 ? void 0 : fieldMessage.props) === null || _a === void 0 ? void 0 : _a.id) {
            return fieldMessage.props.id;
        }
        return fieldMessage ? "".concat(id, "-fieldmessage") : undefined;
    };
    var fieldMessageId = getFieldMessageId();
    if (onTooltipToggle &&
        typeof tooltip !== 'boolean' &&
        typeof tooltip !== 'string') {
        throw new Error('Only use the "onTooltipToggle" prop if you\'re not sending a component of type ' +
            '<Tooltip /> in the "tooltip" prop. If you are, use "onClick" on that component instead');
    }
    if (tooltip && description) {
        throw new Error('Don\'t use both "tooltip" and "description" on an <InputGroup />, pick one of them');
    }
    var isInvalid = !!fieldMessage &&
        (typeof fieldMessage === 'string' ||
            fieldMessage.type === ErrorFieldMessage);
    var hasMessage = !!fieldMessage;
    var ariaDescribedBy = fieldMessageId || descriptionId
        ? [fieldMessageId, descriptionId].filter(Boolean).join(' ')
        : undefined;
    var extraProps = {
        id: id,
        'aria-invalid': isInvalid ? 'true' : 'false',
        'aria-describedby': ariaDescribedBy,
    };
    var modifiedChildren = getChildrenWithExtraProps(children, extraProps);
    return (React.createElement("div", __assign({ className: classNames('ffe-input-group', {
            'ffe-input-group--no-extra-margin': !extraMargin,
            'ffe-input-group--message': hasMessage,
        }, className) }, rest),
        React.createElement("div", { className: "ffe-input-group__header" },
            typeof label === 'string' ? (React.createElement(Label, { htmlFor: id, id: labelId }, label)) : (React.isValidElement(label) &&
                React.cloneElement(label, {
                    htmlFor: id,
                    id: labelId,
                })),
            typeof tooltip === 'string' && (React.createElement(Tooltip, { onClick: onTooltipToggle }, tooltip)),
            tooltip === true && React.createElement(Tooltip, { onClick: onTooltipToggle }),
            React.isValidElement(tooltip) && tooltip),
        description && (React.createElement("div", { className: "ffe-small-text ffe-input-group__description", id: descriptionId }, description)),
        modifiedChildren,
        typeof fieldMessage === 'string' && (React.createElement(ErrorFieldMessage, { as: "p", id: fieldMessageId }, fieldMessage)),
        React.isValidElement(fieldMessage) &&
            React.cloneElement(fieldMessage, {
                id: fieldMessageId,
            })));
};
